import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { expenses, cashAdvances } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Plus, Wallet, Download } from 'lucide-react';
import {
  startOfDay, endOfDay, subDays, startOfWeek, endOfWeek,
  startOfMonth, endOfMonth, subWeeks, subMonths,
} from 'date-fns';
import { downloadExpenseExcel } from '../lib/expenseExcel';
import { calculateDaysOld } from '../lib/format';
import { CashBalanceCard } from '../components/expenses/CashBalanceCard';
import { AllUsersBalances } from '../components/expenses/AllUsersBalances';
import { DateFilter, StatusFilter } from '../components/expenses/ExpenseFilters';
import { ExpenseCharts } from '../components/expenses/ExpenseCharts';
import { ExpenseTable } from '../components/expenses/ExpenseTable';
import { AddExpenseModal, EditExpenseModal } from '../components/expenses/ExpenseFormModals';
import { NoteModal, AuditModal, CashAdvanceModal } from '../components/expenses/ExpenseModals';

const EMPTY_FORM = {
  expense_id: '',
  date: new Date().toISOString().split('T')[0],
  vendor_payee: '',
  category: 'Utilities',
  subcategory: '',
  description: '',
  amount: 0,
  payment_mode: 'UPI',
  ref_no: '',
  notes: '',
};

const EMPTY_ADVANCE = {
  user_email: '',
  amount: 0,
  date: new Date().toISOString().split('T')[0],
  notes: '',
};

const Expenses = () => {
  const { user: currentUser } = useAuth();
  const [expensesList, setExpensesList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [showAuditModal, setShowAuditModal] = useState(false);
  const [showCashAdvanceModal, setShowCashAdvanceModal] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [noteText, setNoteText] = useState('');
  const [filterStatus, setFilterStatus] = useState('All');
  const [datePreset, setDatePreset] = useState('all');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [cashBalance, setCashBalance] = useState(null);
  const [allUsersBalances, setAllUsersBalances] = useState([]);
  const [cashAdvanceData, setCashAdvanceData] = useState(EMPTY_ADVANCE);
  const [formData, setFormData] = useState(EMPTY_FORM);

  const fetchExpenses = useCallback(async () => {
    try {
      const response = await expenses.getAll();
      setExpensesList(response.data);
    } catch (error) {
      console.error('Failed to fetch expenses:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCashBalance = useCallback(async () => {
    if (!currentUser?.email) return;
    try {
      const response = await cashAdvances.getUserBalance(currentUser.email);
      setCashBalance(response.data);
    } catch (error) {
      console.error('Failed to fetch cash balance:', error);
    }
  }, [currentUser]);

  const fetchAllUsersBalances = useCallback(async () => {
    if (currentUser?.role !== 'Admin') return;
    try {
      const response = await cashAdvances.getAllUsersBalances();
      setAllUsersBalances(response.data);
    } catch (error) {
      console.error('Failed to fetch all users balances:', error);
    }
  }, [currentUser]);

  useEffect(() => { fetchExpenses(); }, [fetchExpenses]);

  useEffect(() => {
    fetchCashBalance();
    fetchAllUsersBalances();
  }, [fetchCashBalance, fetchAllUsersBalances, expensesList]);

  const resetForm = () => setFormData(EMPTY_FORM);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await expenses.create({
        ...formData,
        date: new Date(formData.date).toISOString(),
      });
      setShowAddForm(false);
      fetchExpenses();
      resetForm();
    } catch (error) {
      console.error('Failed to create expense:', error);
      alert('Failed to create expense');
    }
  };

  const handleApprove = async (expenseId, approve) => {
    try {
      await expenses.approve(expenseId, approve);
      fetchExpenses();
      fetchCashBalance();
    } catch (error) {
      console.error('Failed to approve expense:', error);
      alert(error.response?.data?.detail || 'Failed to process approval');
    }
  };

  const handleCashAdvanceSubmit = async (e) => {
    e.preventDefault();
    try {
      await cashAdvances.create(cashAdvanceData);
      setShowCashAdvanceModal(false);
      setCashAdvanceData(EMPTY_ADVANCE);
      fetchCashBalance();
      fetchAllUsersBalances();
      alert('Cash advance logged successfully!');
    } catch (error) {
      console.error('Failed to log cash advance:', error);
      alert(error.response?.data?.detail || 'Failed to log cash advance');
    }
  };

  const handleEdit = (expense) => {
    if (expense.is_locked && currentUser?.role !== 'Admin') {
      alert('This expense is locked and cannot be edited.');
      return;
    }
    if (calculateDaysOld(expense.created_at) > 10 && currentUser?.role !== 'Admin') {
      alert('Cannot edit entries older than 10 days. You can only add notes.');
      return;
    }
    setSelectedExpense(expense);
    setFormData({
      expense_id: expense.expense_id,
      date: new Date(expense.date).toISOString().split('T')[0],
      vendor_payee: expense.vendor_payee,
      category: expense.category,
      subcategory: expense.subcategory || '',
      description: expense.description,
      amount: expense.amount,
      payment_mode: expense.payment_mode,
      ref_no: expense.ref_no || '',
      notes: expense.notes || '',
    });
    setShowEditForm(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      await expenses.update(selectedExpense.expense_id, {
        vendor_payee: formData.vendor_payee,
        category: formData.category,
        subcategory: formData.subcategory,
        description: formData.description,
        amount: formData.amount,
        payment_mode: formData.payment_mode,
        ref_no: formData.ref_no,
      });
      setShowEditForm(false);
      fetchExpenses();
      resetForm();
      setSelectedExpense(null);
      alert('Expense updated successfully!');
    } catch (error) {
      console.error('Failed to update expense:', error);
      alert(error.response?.data?.detail || 'Failed to update expense');
    }
  };

  const handleDelete = async (expense) => {
    if (expense.is_locked && currentUser?.role !== 'Admin') {
      alert('This expense is locked and cannot be deleted.');
      return;
    }
    if (calculateDaysOld(expense.created_at) > 10 && currentUser?.role !== 'Admin') {
      alert('Cannot delete entries older than 10 days. You can only add notes.');
      return;
    }
    if (window.confirm(`Are you sure you want to delete expense ${expense.expense_id}? This action cannot be undone.`)) {
      try {
        await expenses.delete(expense.expense_id);
        fetchExpenses();
        alert('Expense deleted successfully!');
      } catch (error) {
        console.error('Failed to delete expense:', error);
        alert(error.response?.data?.detail || 'Failed to delete expense');
      }
    }
  };

  const handleAddNote = (expense) => {
    setSelectedExpense(expense);
    setNoteText('');
    setShowNoteModal(true);
  };

  const handleNoteSubmit = async (e) => {
    e.preventDefault();
    if (!noteText.trim()) {
      alert('Please enter a note');
      return;
    }
    try {
      await expenses.addNote(selectedExpense.expense_id, noteText);
      setShowNoteModal(false);
      setNoteText('');
      setSelectedExpense(null);
      fetchExpenses();
      alert('Note added successfully!');
    } catch (error) {
      console.error('Failed to add note:', error);
      alert('Failed to add note');
    }
  };

  const handleViewAuditLog = (expense) => {
    setSelectedExpense(expense);
    setShowAuditModal(true);
  };

  const dateRange = useMemo(() => {
    const now = new Date();
    switch (datePreset) {
      case 'today':
        return { from: startOfDay(now), to: endOfDay(now), label: 'Today' };
      case 'yesterday': {
        const y = subDays(now, 1);
        return { from: startOfDay(y), to: endOfDay(y), label: 'Yesterday' };
      }
      case 'last_week': {
        const lastWeekRef = subWeeks(now, 1);
        return {
          from: startOfWeek(lastWeekRef, { weekStartsOn: 1 }),
          to: endOfWeek(lastWeekRef, { weekStartsOn: 1 }),
          label: 'Last Week',
        };
      }
      case 'last_month': {
        const lastMonthRef = subMonths(now, 1);
        return { from: startOfMonth(lastMonthRef), to: endOfMonth(lastMonthRef), label: 'Last Month' };
      }
      case 'custom': {
        if (!customFrom || !customTo) return { from: null, to: null, label: 'Custom' };
        return {
          from: startOfDay(new Date(customFrom)),
          to: endOfDay(new Date(customTo)),
          label: `${customFrom} to ${customTo}`,
        };
      }
      case 'all':
      default:
        return { from: null, to: null, label: 'All Time' };
    }
  }, [datePreset, customFrom, customTo]);

  const inRange = useCallback((dateStr) => {
    if (!dateRange.from || !dateRange.to) return true;
    const d = new Date(dateStr);
    return d >= dateRange.from && d <= dateRange.to;
  }, [dateRange]);

  const visibleExpenses = useMemo(() => {
    return [...expensesList]
      .filter((e) => (filterStatus === 'All' ? true : e.status === filterStatus))
      .filter((e) => inRange(e.date))
      .sort((a, b) => {
        const da = new Date(a.entered_at || a.created_at || a.date).getTime();
        const db = new Date(b.entered_at || b.created_at || b.date).getTime();
        return db - da;
      });
  }, [expensesList, filterStatus, inRange]);

  const handleDownloadReport = () =>
    downloadExpenseExcel({ expensesList, dateRange, inRange, allUsersBalances });

  const statusCounts = {
    pending: visibleExpenses.filter(e => e.status === 'Pending').length,
    rejected: visibleExpenses.filter(e => e.status === 'Rejected').length,
  };
  const totalAmount = visibleExpenses.reduce((sum, e) => sum + e.amount, 0);
  const approvedAmount = visibleExpenses.filter(e => e.status === 'Approved').reduce((sum, e) => sum + e.amount, 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-amber-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="expenses-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">Expense Management</h1>
          <p className="text-gray-600 mt-1">Track and approve roastery expenses</p>
        </div>
        <div className="flex gap-3 flex-wrap">
          <button
            onClick={handleDownloadReport}
            className="flex items-center space-x-2 bg-gradient-to-r from-amber-500 to-orange-600 text-white px-6 py-3 rounded-lg font-semibold hover:from-amber-600 hover:to-orange-700 transition shadow-lg"
            data-testid="download-expense-report-button"
          >
            <Download className="w-5 h-5" />
            <span>Download Report</span>
          </button>
          {currentUser?.role === 'Admin' && (
            <button
              onClick={() => setShowCashAdvanceModal(true)}
              className="flex items-center space-x-2 bg-gradient-to-r from-blue-500 to-indigo-600 text-white px-6 py-3 rounded-lg font-semibold hover:from-blue-600 hover:to-indigo-700 transition shadow-lg"
              data-testid="log-cash-advance-button"
            >
              <Wallet className="w-5 h-5" />
              <span>Log Cash Advance</span>
            </button>
          )}
          <button
            onClick={() => setShowAddForm(true)}
            className="flex items-center space-x-2 bg-gradient-to-r from-green-500 to-emerald-600 text-white px-6 py-3 rounded-lg font-semibold hover:from-green-600 hover:to-emerald-700 transition shadow-lg"
            data-testid="add-expense-button"
          >
            <Plus className="w-5 h-5" />
            <span>Add Expense</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <p className="text-sm text-gray-600 font-medium">Total Expenses <span className="text-xs text-gray-400">({dateRange.label})</span></p>
          <p className="text-2xl font-bold text-gray-800 mt-1">₹{totalAmount.toFixed(2)}</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <p className="text-sm text-gray-600 font-medium">Approved <span className="text-xs text-gray-400">({dateRange.label})</span></p>
          <p className="text-2xl font-bold text-green-600 mt-1">₹{approvedAmount.toFixed(2)}</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <p className="text-sm text-gray-600 font-medium">Pending Approval</p>
          <p className="text-2xl font-bold text-yellow-600 mt-1">{statusCounts.pending}</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <p className="text-sm text-gray-600 font-medium">Rejected</p>
          <p className="text-2xl font-bold text-red-600 mt-1">{statusCounts.rejected}</p>
        </div>
      </div>

      <CashBalanceCard cashBalance={cashBalance} />

      {currentUser?.role === 'Admin' && <AllUsersBalances balances={allUsersBalances} />}

      <DateFilter
        datePreset={datePreset} setDatePreset={setDatePreset}
        customFrom={customFrom} setCustomFrom={setCustomFrom}
        customTo={customTo} setCustomTo={setCustomTo}
        dateRange={dateRange}
      />

      <ExpenseCharts visibleExpenses={visibleExpenses} dateRange={dateRange} />

      <StatusFilter filterStatus={filterStatus} setFilterStatus={setFilterStatus} />

      <ExpenseTable
        expenses={visibleExpenses}
        currentUser={currentUser}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onAddNote={handleAddNote}
        onViewAudit={handleViewAuditLog}
        onApprove={handleApprove}
      />

      {showAddForm && (
        <AddExpenseModal
          formData={formData} setFormData={setFormData}
          onSubmit={handleSubmit}
          onClose={() => setShowAddForm(false)}
        />
      )}

      {showEditForm && (
        <EditExpenseModal
          formData={formData} setFormData={setFormData}
          onSubmit={handleEditSubmit}
          onClose={() => { setShowEditForm(false); setSelectedExpense(null); resetForm(); }}
        />
      )}

      {showNoteModal && (
        <NoteModal
          expenseId={selectedExpense?.expense_id}
          noteText={noteText} setNoteText={setNoteText}
          onSubmit={handleNoteSubmit}
          onClose={() => { setShowNoteModal(false); setNoteText(''); setSelectedExpense(null); }}
        />
      )}

      {showAuditModal && selectedExpense && (
        <AuditModal
          expense={selectedExpense}
          onClose={() => { setShowAuditModal(false); setSelectedExpense(null); }}
        />
      )}

      {showCashAdvanceModal && currentUser?.role === 'Admin' && (
        <CashAdvanceModal
          data={cashAdvanceData} setData={setCashAdvanceData}
          onSubmit={handleCashAdvanceSubmit}
          onClose={() => setShowCashAdvanceModal(false)}
        />
      )}
    </div>
  );
};

export default Expenses;
